/**
 * Guia importado de um ficheiro JSON (por exemplo, escrito por uma IA a partir de um pedido
 * como «como criar uma campanha no Google Ads»). Cada ação descreve o elemento como uma
 * pessoa o vê (texto, rótulo, papel): não há seletores gravados nem capturas. Os passos ficam
 * `unverified` e o guia interativo aprende o elemento real na primeira vez que alguém os segue.
 *
 * Por agora só o proprietário da aplicação importa (app web, canImportGuide).
 */
import type { Locale } from '@nolens/i18n';
import type { Step, TextTone } from './steps';
import type { Target } from './target';

export const IMPORT_FORMAT = 'nolens-guide/1';
export const IMPORT_MAX_STEPS = 500;

/** Como o ficheiro descreve um elemento da página. */
export interface ImportElement {
  /** Texto visível (botões, ligações, separadores, itens de menu). */
  text?: string;
  /** Rótulo de um campo de formulário. */
  label?: string;
  ariaLabel?: string;
  placeholder?: string;
  name?: string;
  /** Papel ARIA (button, link, tab, menuitem, textbox, combobox, checkbox…). */
  role?: string;
  /** Seletor CSS, se for conhecido (opcional; raramente estável). */
  selector?: string;
}

export type ImportAction = 'open' | 'click' | 'doubleclick' | 'type' | 'enter' | 'select' | 'note';

export interface ImportStep {
  action: ImportAction;
  url?: string;
  element?: ImportElement;
  /** type: o texto a escrever (um exemplo; nunca uma palavra-passe). */
  value?: string;
  /** select: o texto da opção. */
  option?: string;
  /** Título do passo; vazio = título automático. */
  title?: string;
  /** Nota do passo (ou o texto de um bloco «note»). */
  note?: string;
  text?: string;
  tone?: TextTone;
}

export interface ImportFile {
  format: typeof IMPORT_FORMAT;
  title: string;
  description?: string;
  language?: Locale;
  steps: ImportStep[];
}

export type ImportError =
  | { code: 'json' }
  | { code: 'format' }
  | { code: 'noSteps' }
  | { code: 'tooMany' }
  | { code: 'noOpen' }
  | { code: 'step'; index: number; reason: 'action' | 'url' | 'element' | 'option' | 'text' };

export interface ImportedGuide {
  title: string;
  description: string;
  language?: Locale;
  steps: Step[];
}

const ACTIONS: readonly ImportAction[] = ['open', 'click', 'doubleclick', 'type', 'enter', 'select', 'note'];
const TONES: readonly TextTone[] = ['tip', 'alert', 'info', 'heading'];
const LANGS: readonly Locale[] = ['en', 'pt-PT'];
const KIND: Record<Exclude<ImportAction, 'note'>, Step['kind']> = {
  open: 'navigate',
  click: 'click',
  doubleclick: 'dblclick',
  type: 'type',
  enter: 'key',
  select: 'select',
};

const str = (v: unknown, max: number): string =>
  typeof v === 'string' ? v.replace(/\s+/g, ' ').trim().slice(0, max) : '';
const para = (v: unknown, max: number): string =>
  typeof v === 'string' ? v.trim().slice(0, max) : '';

function httpUrl(v: unknown): string {
  const s = str(v, 4000);
  try {
    const u = new URL(s);
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.href : '';
  } catch {
    return '';
  }
}

/** Descrição do ficheiro → Target sem seletores gravados (procura pelo texto e pelo papel). */
function toTarget(e: unknown): Target | null {
  if (!e || typeof e !== 'object') return null;
  const o = e as Record<string, unknown>;
  const t: Target = { selectors: [], framePath: [], shadowPath: [] };
  const sel = str(o.selector, 1000);
  if (sel) t.selectors.push(sel);
  const fields = { text: str(o.text, 80), label: str(o.label, 500), ariaLabel: str(o.ariaLabel, 500), placeholder: str(o.placeholder, 500), name: str(o.name, 200) };
  for (const [k, v] of Object.entries(fields)) if (v) (t as unknown as Record<string, string>)[k] = v;
  const role = str(o.role, 60).toLowerCase();
  if (/^[a-z]+$/.test(role)) t.role = role;
  return sel || Object.values(fields).some(Boolean) ? t : null;
}

/** O texto colado pode vir com explicações ou entre ``` à volta: fica só o objeto JSON. */
function jsonOf(text: string): unknown {
  const a = text.indexOf('{');
  const b = text.lastIndexOf('}');
  if (a === -1 || b <= a) throw new SyntaxError('no object');
  return JSON.parse(text.slice(a, b + 1));
}

/** Lê o ficheiro e converte-o nos passos de um guia novo. */
export function parseImport(
  text: string,
  newId: () => string = () => crypto.randomUUID(),
  now = Date.now(),
): { ok: true; guide: ImportedGuide } | { ok: false; error: ImportError } {
  let data: unknown;
  try {
    data = jsonOf(text);
  } catch {
    return { ok: false, error: { code: 'json' } };
  }
  const f = data as Partial<ImportFile> | null;
  if (!f || typeof f !== 'object' || f.format !== IMPORT_FORMAT) return { ok: false, error: { code: 'format' } };
  if (!Array.isArray(f.steps) || !f.steps.length) return { ok: false, error: { code: 'noSteps' } };
  if (f.steps.length > IMPORT_MAX_STEPS) return { ok: false, error: { code: 'tooMany' } };
  const steps: Step[] = [];
  let url = '';
  for (let i = 0; i < f.steps.length; i++) {
    const raw = f.steps[i] as Partial<ImportStep> | null;
    const fail = (reason: 'action' | 'url' | 'element' | 'option' | 'text') =>
      ({ ok: false, error: { code: 'step', index: i, reason } }) as const;
    if (!raw || typeof raw !== 'object' || !ACTIONS.includes(raw.action as ImportAction)) return fail('action');
    const base = { id: newId(), title: str(raw.title, 300), description: para(raw.note, 5000), pageTitle: '', createdAt: now + i };
    if (raw.action === 'note') {
      const body = para(raw.text ?? raw.note, 5000);
      if (!base.title && !body) return fail('text');
      const tone = TONES.includes(raw.tone as TextTone) ? (raw.tone as TextTone) : 'tip';
      steps.push({ ...base, kind: 'text', tone, description: body, url: '' });
      continue;
    }
    if (raw.action === 'open') {
      url = httpUrl(raw.url);
      if (!url) return fail('url');
      steps.push({ ...base, kind: 'navigate', url, unverified: true });
      continue;
    }
    if (!url) return { ok: false, error: { code: 'noOpen' } };
    const target = toTarget(raw.element);
    if (!target) return fail('element');
    const step: Step = { ...base, kind: KIND[raw.action as keyof typeof KIND], url, target, unverified: true };
    if (raw.action === 'enter') step.key = 'Enter';
    if (raw.action === 'type') {
      const v = str(raw.value, 200);
      if (v) step.typed = v;
    }
    if (raw.action === 'select') {
      const o = str(raw.option, 500);
      if (!o) return fail('option');
      step.option = { text: o, value: '' };
    }
    steps.push(step);
  }
  if (!url) return { ok: false, error: { code: 'noOpen' } };
  return {
    ok: true,
    guide: {
      title: str(f.title, 200),
      description: para(f.description, 5000),
      ...(LANGS.includes(f.language as Locale) ? { language: f.language } : {}),
      steps,
    },
  };
}

const LANGUAGE_NAME: Record<Locale, string> = { en: 'English', 'pt-PT': 'European Portuguese (pt-PT)' };

/**
 * Instruções para colar numa IA antes do pedido («Como criar uma campanha de pesquisa no
 * Google Ads»): a resposta é o ficheiro a importar.
 */
export function importPrompt(language: Locale): string {
  return `You write step-by-step guides for Nolens Guides, a tool that highlights each element on the real website while a person follows the guide. Reply with ONLY one JSON object (no explanations, no code fences) in this format:

{
  "format": "${IMPORT_FORMAT}",
  "title": "Short title of the task",
  "description": "One sentence on what the guide does",
  "language": "${language}",
  "steps": [
    { "action": "open", "url": "https://example.com/start" },
    { "action": "click", "element": { "text": "New campaign", "role": "button" } },
    { "action": "type", "element": { "label": "Campaign name" }, "value": "Spring sale" },
    { "action": "select", "element": { "label": "Country" }, "option": "Portugal" },
    { "action": "enter", "element": { "label": "Search" } },
    { "action": "doubleclick", "element": { "text": "Report.pdf" } },
    { "action": "note", "tone": "tip", "title": "Before you continue", "text": "Have your billing details at hand." }
  ]
}

Rules:
- The first step is "open" with the full https address where the task starts. Add another "open" only when the person really goes to a different address.
- Describe each element exactly as it appears on the screen: "text" is the visible text of buttons, links, tabs and menu items, copied word for word in the language of the site's interface; "label" is the label of a form field. Add "role" (button, link, tab, menuitem, textbox, combobox, checkbox, radio, option) when you know it. Only add "ariaLabel", "placeholder" or "selector" if you are sure of them.
- One action per step. Open menus and dialogs with their own "click" step before clicking what is inside them.
- "title" and "note" are optional: leave them out to get an automatic title ("Click “New campaign”"). Use "note" for what the person needs to know or decide in that step.
- Use "note" steps (tone tip, alert, info or heading) for advice and warnings, not for actions.
- "value" is an example of what to type. Never include passwords, card numbers or other personal data.
- Write titles, notes and the description in ${LANGUAGE_NAME[language]}.
- If you are not sure how the current interface looks, still give your best guess: the person corrects any step the first time they follow the guide.

The task:
`;
}
